"use client";

import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
  useCallback,
  Suspense,
  lazy,
  type ComponentType,
} from "react";
import { usePathname } from "next/navigation";
import { useTheme } from "@/providers/theme";
import { getPortalSession } from "@/app/actions";
import BootOverlay from "@/components/boot-overlay";
import { LIBUM_AVATAR_NAMES } from "@/lib/constants";

import { useAccountReadiness } from "./account-readiness";
import { AccountRailTransition } from "@/components/account-rail-transition";
import { CachedAccountRail } from "@/components/docs/cached-rail";
import {
  readAccountRail,
  writeAccountRail,
  type AccountRailSnapshot,
} from "@/lib/account-rail-cache";

import { DocsSearchStateProvider } from "./docs-search-state";

const GuestAvatarContext = createContext<string | null>(null);
export function useDocsGuestAvatar() {
  return useContext(GuestAvatarContext);
}

type Session = Awaited<ReturnType<typeof getPortalSession>>;
export const SessionContext = createContext<{
  session: Session | null;
  resolving: boolean;
  updateSession: (session: Session) => void;
}>({ session: null, resolving: true, updateSession: () => {} });
export function useDocsAccountPending() {
  const { session } = useContext(SessionContext);
  // Also retain the pending rail while the authenticated controls load their data.
  return !!session?.token;
}
export function useDocsHasSession() {
  return !!useContext(SessionContext).session?.token;
}
const AccountRuntime = lazy(() => import("@/providers/account-runtime"));
export const AccountRuntimeContext = createContext<ComponentType<{
  placement: "sidebar" | "header";
  fallback: React.ReactNode;
}> | null>(null);
export function useAccountSession() {
  return useContext(SessionContext);
}

/** Root-owned account state survives public/protected layout changes. Guests
 * render public content immediately without loading the realtime runtime. */
export function AccountSessionProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const pathname = usePathname();
  const isProtected = ["/settings", "/poweron-pro", "/glaci", "/insights"].some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
  const [session, setSession] = useState<Session | null>(null);
  const [resolving, setResolving] = useState(true);
  const [guestAvatar, setGuestAvatar] = useState<string | null>(null);
  const { setFollowSystem } = useTheme();
  const signedIn = !!session?.token;
  const updateSession = useCallback((next: Session) => setSession(next), []);
  useEffect(() => {
    if (resolving) return;
    setFollowSystem(!signedIn);
    try {
      localStorage.setItem(
        "portal-theme-session",
        signedIn ? "signed-in" : "guest",
      );
    } catch {
      // The theme still works when browser storage is unavailable.
    }
    // Preserve the current mode during navigation while the next session resolves.
  }, [resolving, signedIn, setFollowSystem]);

  useEffect(() => {
    setGuestAvatar(
      LIBUM_AVATAR_NAMES[Math.floor(Math.random() * LIBUM_AVATAR_NAMES.length)],
    );
  }, []);
  useEffect(() => {
    let active = true;
    let pending = false;
    let refreshAgain = false;
    const refresh = async () => {
      if (pending) {
        refreshAgain = true;
        return;
      }
      pending = true;
      let confirmed = false;
      try {
        const next = await getPortalSession();
        confirmed = true;
        if (active) setSession(next);
      } catch {
        // Account availability must never block public documentation.
        // A failed request is not a sign-out. Retain a verified session.
      } finally {
        if (active && confirmed) setResolving(false);
        pending = false;
        if (active && refreshAgain) {
          refreshAgain = false;
          void refresh();
        }
      }
    };
    void refresh();
    window.addEventListener("focus", refresh);
    window.addEventListener("portal-session-changed", refresh);
    return () => {
      active = false;
      window.removeEventListener("focus", refresh);
      window.removeEventListener("portal-session-changed", refresh);
    };
  }, []);
  return (
    <SessionContext.Provider value={{ session, resolving, updateSession }}>
      <DocsSearchStateProvider>
        <GuestAvatarContext.Provider value={guestAvatar}>
          {session && (session.token || isProtected) ? (
            <Suspense fallback={children}>
              <AccountRuntime>{children}</AccountRuntime>
            </Suspense>
          ) : (
            children
          )}
          {isProtected && <BootOverlay />}
        </GuestAvatarContext.Provider>
      </DocsSearchStateProvider>
    </SessionContext.Provider>
  );
}

export function DocsAccountControls({
  children,
  placement = "sidebar",
}: {
  children: React.ReactNode;
  placement?: "sidebar" | "header";
}) {
  const { session, resolving } = useContext(SessionContext);
  const AccountControls = useContext(AccountRuntimeContext);
  const accountReady = useAccountReadiness();
  const [snapshot, setSnapshot] = useState<AccountRailSnapshot | null>(null);
  const [hydrated, setHydrated] = useState(false);
  useLayoutEffect(() => {
    setSnapshot(readAccountRail());
    setHydrated(true);
    const refreshSnapshot = () => {
      const next = readAccountRail();
      // Keep the outgoing appearance until the confirmed guest transition finishes.
      if (next) setSnapshot(next);
    };
    window.addEventListener("portal-account-rail-changed", refreshSnapshot);
    return () =>
      window.removeEventListener(
        "portal-account-rail-changed",
        refreshSnapshot,
      );
  }, []);
  const confirmedGuest = !resolving && !session?.token;
  const [showCached, setShowCached] = useState(true);
  useEffect(() => {
    if (confirmedGuest) {
      setSnapshot((current) => current ?? readAccountRail());
      writeAccountRail(null);
      setShowCached(false);
    }
  }, [confirmedGuest]);
  const live = !!session?.token && !!AccountControls && accountReady;
  if (placement === "header") {
    return live && AccountControls ? (
      <AccountControls placement={placement} fallback={children} />
    ) : (
      <>{children}</>
    );
  }
  if (!hydrated)
    return (
      <div className="rail-bootstrap flex h-full w-12 shrink-0 md:w-[calc(var(--sidebar-width-icon)_+_1px)]">
        <div className="rail-bootstrap-cached h-full w-full">
          <CachedAccountRail
            bootstrap
            snapshot={{
              insights: true,
              upgrade: true,
              avatar: null,
              organizationLogo: null,
            }}
          />
        </div>
        <div className="rail-bootstrap-guest flex h-full w-full">
          {children}
        </div>
      </div>
    );
  return (
    <AccountRailTransition
      ready={live || (!!snapshot && showCached)}
      fallback={children}
    >
      {live && AccountControls ? (
        <AccountControls placement={placement} fallback={children} />
      ) : snapshot ? (
        <CachedAccountRail snapshot={snapshot} />
      ) : null}
    </AccountRailTransition>
  );
}
